import { Component, ElementRef, EventEmitter, OnInit, Output, Renderer2 } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { User } from '../models/user';
import { AuthService } from '../services/authService';
import { Router } from '@angular/router';
import { UserNewFoodEntryModel } from '../models/userNewFoodEntryModel';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, Observable, of } from 'rxjs';

@Component({
  selector: 'app-create-new-food',
  templateUrl: './create-new-food.component.html',
  styleUrl: './create-new-food.component.scss',
  standalone: false
})
export class CreateNewFoodComponent implements OnInit {
  isFoodAddedSuccessfully = false;
  newFoodForm = new FormGroup({
    name: new FormControl("", [
      Validators.required,
      Validators.maxLength(150),
    ]),
    servingSize: new FormControl("", [
      Validators.required,
      Validators.min(1),
      Validators.max(2000),
    ]),
    unitsOfMeasureId: new FormControl("", [
      Validators.required,
      Validators.min(1),
      Validators.max(10),
    ]),
    carbs: new FormControl("", [
      Validators.required,
      Validators.min(0),
      Validators.max(1000),
    ]),
    alcohol: new FormControl("", [
      Validators.required,
      Validators.min(0),
      Validators.max(1000),
    ]),
    fiber: new FormControl("", [
      Validators.required,
      Validators.min(0),
      Validators.max(1000),
    ]),
    netcarbs: new FormControl("", [
      Validators.required,
      Validators.min(0),
      Validators.max(1000),
    ]),
    fat: new FormControl("", [
      Validators.required,
      Validators.min(0),
      Validators.max(1000),
    ]),
    protein: new FormControl("", [
      Validators.required,
      Validators.min(0),
      Validators.max(1000),
    ]),
    calories: new FormControl("", [
      Validators.required,
      Validators.min(0),
      Validators.max(10000),
    ]),
  });
  user = new User('', false);
  radioModel = '1';
  modelGroupDisabled = false;
  btnRadio = '1';
  response: any;
  renderer: any;
  duplicateFoodName: boolean = false;
  somethingWentWrong: boolean = false;
  @Output() foodCreated = new EventEmitter<void>();

  constructor(private authService: AuthService, private http: HttpClient, public el: ElementRef, renderer: Renderer2, public router: Router) {
    this.renderer = renderer;
  }

  async ngOnInit(): Promise<void> {
    const isLoggedIn = await this.checkIfUserIsLoggedIn();
    if (!isLoggedIn) {
      // Redirect to login page
      this.router.navigate(['/login']);
    }
    this.newFoodForm.controls.unitsOfMeasureId.setValue('');
    this.newFoodForm.controls.calories.disable();
    this.newFoodForm.controls.netcarbs.disable();
  }

  private checkIfUserIsLoggedIn(): Promise<boolean> {
    return new Promise((resolve) => {
      this.authService.isLoggedIn().subscribe((response: any) => {
        this.user.loggedin = response.isAuthenticated;
        this.user.username = response.userName ?? "";
        resolve(this.user.loggedin);
      });
    });
  }

  public saveNewFood():void {
    const userNewFoodEntryModel = new UserNewFoodEntryModel(
      this.newFoodForm.controls.name.value ?? '',
      Number(this.newFoodForm.controls.unitsOfMeasureId.value) ?? 0,
      Number(this.newFoodForm.controls.servingSize.value) ?? 0,
      Number(this.newFoodForm.controls.carbs.value) ?? 0,
      Number(this.newFoodForm.controls.fiber.value) ?? 0,
      Number(this.newFoodForm.controls.netcarbs.value) ?? 0,
      Number(this.newFoodForm.controls.fat.value) ?? 0,
      Number(this.newFoodForm.controls.protein.value) ?? 0,
      Number(this.newFoodForm.controls.alcohol.value) ?? 0,
      Number(this.newFoodForm.controls.calories.value) ?? 0
    );

    const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
    this.http
      .post('/api/Foods/create_new_food', userNewFoodEntryModel, {
        headers: headers,
      })
      .pipe(catchError(this.handleError<any>('saveNewFood')))
      .subscribe({
        next: (response) => {
          this.response = response;
          if (!response || Object.keys(response).length === 0) {
            console.error('response is undefined or empty');
            this.isFoodAddedSuccessfully = false;
            this.somethingWentWrong = true;
          } else if (response?.success == false) {
            this.isFoodAddedSuccessfully = false;
            this.somethingWentWrong = true;
            for (
              let errorMessage = 0;
              errorMessage < response.errorMessages.length;
              errorMessage++
            ) {
              console.error(response.errorMessages[errorMessage]);
              const errorMsgLowerCase = response.errorMessages[errorMessage];
              if (errorMsgLowerCase.indexOf("A food entry with the same name already exists.") != -1) {
                this.duplicateFoodName = true;
              }

              if (errorMsgLowerCase.indexOf('user id not found.') != -1 || errorMsgLowerCase.indexOf('user is not authenticated.') != -1) {
                this.router.navigate(['/login']);
              }
            }
          } else {
            // success
            this.isFoodAddedSuccessfully = true;
            this.somethingWentWrong = false;
            this.foodCreated.emit();
          }
        },
        error: (error) => {
          this.isFoodAddedSuccessfully = false;
          this.somethingWentWrong = true;
          this.response = error;
          console.error('Error received:', error.message);
        },
      });
  }

  public updateNetCarbs() {
    const carbs = this.newFoodForm.controls.carbs.value ? Number(this.newFoodForm.controls.carbs.value) : 0;
    const fiber = this.newFoodForm.controls.fiber.value ? Number(this.newFoodForm.controls.fiber.value) : 0;
    let netCarbs = carbs - fiber;
    netCarbs = (netCarbs < 0) ? 0 : netCarbs;
    this.newFoodForm.controls.netcarbs.setValue(netCarbs.toString());
    this.updateCalories();
  }

  public updateCalories() {
    const netCarbs = this.newFoodForm.controls.netcarbs.value ? Number(this.newFoodForm.controls.netcarbs.value) : 0;
    const fat = this.newFoodForm.controls.fat.value ? Number(this.newFoodForm.controls.fat.value) : 0;
    const protein = this.newFoodForm.controls.protein.value ? Number(this.newFoodForm.controls.protein.value) : 0;
    const alcohol = this.newFoodForm.controls.alcohol.value ? Number(this.newFoodForm.controls.alcohol.value) : 0;
    const calories = (netCarbs * 4) + (fat * 9) + (protein * 4) + (alcohol * 7);
    this.newFoodForm.controls.calories.setValue(calories.toString());
  }

  public handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(error);
      return of(result as T);
    };
  }
}
